import { Icon } from "@mdi/react";
import { useEffect, useRef, useState } from "react";

import { mdiLayers } from "@mdi/js";

import useOnClickOutside from "hooks/useOnClickOutside";

import { BaseButton } from "components/base";

import { BaseLayerKind } from "../contexts/MapContextState";
import { useMapContext } from "../hooks/useMapContext";
import { useMapDispatch } from "../hooks/useMapDispatch";
import { BASE_LAYER_OPTIONS, getBaseLayerStyleUrl } from "../utils/getBaseLayerStyleUrl";
import styles from "./BaseLayerToggle.module.scss";

export default function BaseLayerToggle() {
  const { mapbox, baseLayer } = useMapContext();
  const mapDispatch = useMapDispatch();
  // Tracks the previous mapbox value so we can distinguish "mapbox just arrived
  // from null (already initialized with the correct style)" from "baseLayer
  // changed on an existing map instance (user switched styles)".
  // 'uninitialized' sentinel means the effect has never run yet.
  const prevMapboxRef = useRef<typeof mapbox | "uninitialized">("uninitialized");
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useOnClickOutside(rootRef, () => setOpen(false));

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!mapbox) {
      prevMapboxRef.current = null;
      return;
    }
    const prevMapbox = prevMapboxRef.current;
    prevMapboxRef.current = mapbox;
    // prevMapbox === null means mapbox just transitioned from null → defined,
    // i.e. createMapbox() just ran and already applied the correct style.
    if (prevMapbox === null) {
      return;
    }
    const zoom = mapbox.getZoom();
    mapbox.setStyle(getBaseLayerStyleUrl(baseLayer));
    mapbox.setZoom(zoom);
  }, [mapbox, baseLayer]);

  if (!mapbox) {
    return null;
  }

  const select = (value: BaseLayerKind) => {
    mapDispatch({ payload: { baseLayer: value } });
    setOpen(false);
  };

  return (
    <div className={`base-layer-toggle ${styles.baseLayerToggleWrapper}`}>
      <div ref={rootRef} className={styles.dropdownContainer}>
        <ul
          className={`${styles.dropdownMenu} ${open ? styles.menuOpen : ""}`}
          role="listbox"
          aria-label="Map style"
          aria-hidden={!open}>
          {BASE_LAYER_OPTIONS.map((option) => {
            const selected = baseLayer === option.value;
            return (
              <li
                key={option.value}
                data-testid={`base-layer-option-${option.value.toLowerCase()}`}
                className={`${styles.optionContainer} ${
                  selected ? styles.optionContainerSelected : ""
                }`}
                role="option"
                aria-selected={selected}
                tabIndex={open ? 0 : -1}
                onClick={() => select(option.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    select(option.value);
                  }
                }}>
                <div
                  className={styles.optionPreview}
                  style={
                    option.preview
                      ? { backgroundImage: `url(${option.preview})` }
                      : undefined
                  }
                />
                {option.label}
              </li>
            );
          })}
        </ul>
        <BaseButton
          appearance="none"
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid="base-layer-toggle-trigger"
          className={`${styles.triggerButton} ${open ? styles.triggerOpen : ""}`}
          iconBefore={<Icon path={mdiLayers} size={"16px"} />}
          onClick={() => setOpen((o) => !o)}>
          Map Style
        </BaseButton>
      </div>
    </div>
  );
}
